import { FC, useMemo } from "react";
import DashboardTile from "@components/Home/DashboardTile/DashboardTile";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { RootState } from "@store/store";
import { useGetCompanionsQuery } from "@store/companionApi";
import styled from "@emotion/styled";
import { Box, Skeleton, Stack, Typography } from "@mui/material";
import { useGetSessionsQuery } from "@store/sessionApi";
import { Order } from "@models/page-options";
import { DateUtil } from "@utils/date.util";
import TextIconButton from "@components/TextIconButton";
import LocalPhoneOutlinedIcon from "@mui/icons-material/LocalPhoneOutlined";
import { useNavigate } from "react-router-dom";
import { Mode } from "@store/homeSlice";

const AvatarPicture = styled.img`
  object-fit: cover;
  object-position: top;
  height: 100%;
  width: 100%;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const Footer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-top: auto;
  align-items: center;
`;

const GhostLoader: FC = () => {
  return (
    <Box display="flex" flexDirection="column" gap="16px">
      <Skeleton variant="rectangular" height="300px" />
      <Skeleton variant="rectangular" height="64px" />
    </Box>
  );
};

const CompanionTile: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const mode = useSelector((state: RootState) => state.home.mode);
  const patientId = useSelector(
    (state: RootState) => state.home.selectedPatientId,
  );
  const userId = useSelector((state: RootState) => state.auth.user?.id);
  const { data: companions } = useGetCompanionsQuery(
    { patientId: (mode ===  Mode.Patient ? patientId : undefined) ?? undefined, userId: mode ===  Mode.Caregiver ? userId! : undefined},
    {
      skip: !patientId && !userId,
    },
  );

  const { data: sessions } = useGetSessionsQuery(
    { patientId: patientId!, order: Order.DESC, page: 1, pageSize: 1 },
    {
      skip: !patientId,
    },
  );

  const defaultCompanion = useMemo(
    () => companions?.data?.find(({ isDefault }) => isDefault),
    [companions],
  );

  const lastSession = useMemo(() => sessions?.data?.[0], [sessions]);

  const lastSessionDate = useMemo(
    () =>
      lastSession ? DateUtil.formatTime(lastSession.createdDate) : t("never"),
    [lastSession, t],
  );

  return (
    <DashboardTile
      title={t("dashboard.companion.title")}
    >
      {defaultCompanion && sessions ? (
        <Content>
          <Box flex="1" gap="24px" display="flex" alignItems="center">
            <AvatarPicture
              src={defaultCompanion.avatar.imageUrl}
              alt={defaultCompanion.name}
            />
          </Box>
          <Footer>
            <Stack
              flexDirection="row"
              justifyContent="space-between"
              width="100%"
              gap="8px"
              flexWrap="wrap"
            >
              <Typography
                fontSize="20px"
                fontWeight="600"
                color="textSecondary"
                alignContent="center"
              >
                {defaultCompanion.name}
              </Typography>

              <Typography
                fontSize="14px"
                fontWeight="400"
                color="#171A1FFF"
                alignContent="center"
              >
                {t("dashboard.companion.lastEngaged", {
                  date: lastSessionDate,
                })}
              </Typography>
            </Stack>
            <Box marginTop="32px" width="100%">
              <TextIconButton
                variant="contained"
                color="success"
                size="large"
                fullWidth
                iconLeft={true}
                text={t("dashboard.companion.startCallButton", { name: defaultCompanion.name })}
                icon={LocalPhoneOutlinedIcon}
                onClick={() => navigate(mode === Mode.Patient? "/portal/talk" : "/talk")}
                id={mode === Mode.Patient? "patient-call-button" : "caregiver-call-button"}
              />
            </Box>
          </Footer>
        </Content>
      ) : (
        <GhostLoader />
      )}
    </DashboardTile>
  );
};

export default CompanionTile;
